import { useState } from 'react'
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import DashboardIcon from '@mui/icons-material/GridViewRounded'
import CalculateIcon from '@mui/icons-material/CalculateRounded'
import FolderIcon from '@mui/icons-material/FolderRounded'
import LogoutIcon from '@mui/icons-material/LogoutRounded'
import VisibilityIcon from '@mui/icons-material/VisibilityRounded'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffRounded'
import PrivacyToggle from './PrivacyToggle'
import { toggleAmountsHidden, useAmountsHidden } from '../privacy'
import logo from '../assets/patrimo-logo-dark-bg.svg'
import mark from '../assets/patrimo-mark-dark-bg.svg'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import { Link as RouterLink, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { palette } from '../theme'

const drawerWidth = 244

const navItems = [
  { to: '/', label: 'Tableau de bord', icon: <DashboardIcon />, end: true },
  {
    to: '/simulations',
    label: 'Simulations',
    icon: <CalculateIcon />,
    end: false,
  },
  { to: '/projects', label: 'Projets', icon: <FolderIcon />, end: false },
]

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, logout } = useAuth()
  const hideAmounts = useAmountsHidden()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const items =
    user?.role === 'admin'
      ? [
          ...navItems,
          {
            to: '/admin',
            label: 'Administration',
            icon: <AdminIcon />,
            end: false,
          },
        ]
      : navItems

  const initials = (user?.email ?? '?').slice(0, 2).toUpperCase()

  const logoLink = (src: string) => (
    <Box
      component={RouterLink}
      to="/"
      onClick={() => setMobileOpen(false)}
      sx={{ display: 'flex', lineHeight: 0 }}
    >
      <Box component="img" src={src} alt="Patrimo" sx={{ height: 36, width: 'auto' }} />
    </Box>
  )

  const sidebar = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: palette.sidebar,
      }}
    >
      <Toolbar>{logoLink(logo)}</Toolbar>
      <List sx={{ px: 1.5, pt: 3, flexGrow: 1 }}>
        {items.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            disableRipple
            sx={{
              borderRadius: 2,
              mb: 0.75,
              py: 1,
              color: 'text.secondary',
              '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 38 },
              '&:hover': {
                bgcolor: 'rgba(245,245,245,0.05)',
                color: 'text.primary',
              },
              '&.active': {
                bgcolor: palette.sidebarActive,
                color: 'text.primary',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 600 } } }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* On mobile the toggle lives in the top bar */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, px: 1.5, pb: 1.5 }}>
        <ListItemButton
          onClick={toggleAmountsHidden}
          disableRipple
          sx={{
            borderRadius: 2,
            py: 1,
            color: 'text.secondary',
            '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 38 },
            '&:hover': { bgcolor: 'rgba(245,245,245,0.05)', color: 'text.primary' },
          }}
        >
          <ListItemIcon>{hideAmounts ? <VisibilityOffIcon /> : <VisibilityIcon />}</ListItemIcon>
          <ListItemText
            primary={hideAmounts ? 'Afficher les montants' : 'Masquer les montants'}
            slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 600 } } }}
          />
        </ListItemButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(245,245,245,0.10)', mx: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 2 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: palette.card, fontSize: 13 }}>
          {initials}
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {user?.email}
        </Typography>
        <Tooltip title="Déconnexion">
          <IconButton
            aria-label="Déconnexion"
            onClick={handleLogout}
            sx={{ color: 'text.secondary', p: 1.5 }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: palette.background }}>
      {/* Mobile only: on desktop the sidebar carries logo, nav and account actions */}
      <AppBar position="fixed" sx={{ display: { md: 'none' } }}>
        <Toolbar>
          {logoLink(mark)}
          <Box sx={{ flexGrow: 1 }} />
          <PrivacyToggle />
          <IconButton
            color="inherit"
            edge="end"
            size="large"
            aria-label="Ouvrir le menu"
            onClick={() => setMobileOpen(true)}
            sx={{ ml: 1 }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: drawerWidth },
        }}
      >
        {sidebar}
      </Drawer>

      <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            bgcolor: palette.background,
            overflowX: 'hidden',
          }}
        >
          {/* Spacer so the fixed app bar doesn't cover the content */}
          <Toolbar sx={{ display: { md: 'none' } }} />
          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Outlet />
          </Box>
        </Box>

        <Box
          component="nav"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            height: '100dvh',
            alignSelf: 'flex-start',
            borderLeft: '1px solid rgba(245,245,245,0.08)',
          }}
        >
          {sidebar}
        </Box>
      </Box>
    </Box>
  )
}
